import { DragEvent, useCallback, useMemo, useState } from 'react';

interface UseFileDropTargetOptions {
  onFiles: (files: File[]) => void;
  disabled?: boolean;
  accept?: string;
  onRejected?: (files: File[]) => void;
}

export interface FileDropTargetProps {
  onDragOver: (e: DragEvent<HTMLElement>) => void;
  onDragLeave: (e: DragEvent<HTMLElement>) => void;
  onDrop: (e: DragEvent<HTMLElement>) => void;
}

interface UseFileDropTargetResult {
  isDragActive: boolean;
  dropTargetProps: FileDropTargetProps;
}

const hasFilePayload = (e: DragEvent<HTMLElement>): boolean =>
  Array.from(e.dataTransfer?.types ?? []).includes('Files');

const matchesAccept = (file: File, accept: string): boolean =>
  accept.split(',').some((token) => {
    const pattern = token.trim().toLowerCase();
    if (!pattern) return false;
    if (pattern.startsWith('.')) return file.name.toLowerCase().endsWith(pattern);
    if (pattern.endsWith('/*')) return file.type.toLowerCase().startsWith(pattern.slice(0, -1));
    return file.type.toLowerCase() === pattern;
  });

export const useFileDropTarget = ({
  onFiles,
  disabled,
  accept,
  onRejected,
}: UseFileDropTargetOptions): UseFileDropTargetResult => {
  const [isDragActive, setIsDragActive] = useState(false);

  const onDragOver = useCallback(
    (e: DragEvent<HTMLElement>) => {
      if (disabled || !hasFilePayload(e)) return;
      e.preventDefault();
      e.stopPropagation();
      setIsDragActive(true);
    },
    [disabled],
  );

  const onDragLeave = useCallback((e: DragEvent<HTMLElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.currentTarget.contains(e.relatedTarget as Node | null)) return;
    setIsDragActive(false);
  }, []);

  const onDrop = useCallback(
    (e: DragEvent<HTMLElement>) => {
      setIsDragActive(false);
      if (disabled || !hasFilePayload(e)) return;
      e.preventDefault();
      e.stopPropagation();
      const files = Array.from(e.dataTransfer.files ?? []);
      if (files.length === 0) return;
      const accepted = accept ? files.filter((f) => matchesAccept(f, accept)) : files;
      const rejected = accept ? files.filter((f) => !matchesAccept(f, accept)) : [];
      if (rejected.length > 0) onRejected?.(rejected);
      if (accepted.length > 0) onFiles(accepted);
    },
    [disabled, onFiles, accept, onRejected],
  );

  const dropTargetProps = useMemo(
    () => ({ onDragOver, onDragLeave, onDrop }),
    [onDragOver, onDragLeave, onDrop],
  );

  return { isDragActive, dropTargetProps };
};
